import { useIsLoggedInQuery } from 'auth/hooks'
import { useHasAllAccessQuery } from '@repo/cf-user/hooks/use-user-data'
import { useCoinBalance } from '@repo/studio-hooks-coins/use-coin-balance'
import { useIsOnAnyTrialQuery } from '@repo/studio-hooks-user/use-is-paid-subscriber'
import { Surface } from '@repo/rpc-types-creativefabrica-common-v1/surface/v1'
import { useIsStudioAIUserQuery } from '@repo/studio-hooks-studio-ai/use-is-studio-ai-user'
import { useGetAutoTopUpSettingsQuery } from '@repo/studio-hooks-coins/gen/service/creativefabrica/coins/v2'

export interface UseAutoTopUpPromptBannerQueryInput {
  nextGenerationCost: number | undefined
  isNextGenerationCostPending: boolean
  /**
   * Omitted means Studio. Marketplace tools pass `Surface.MARKETPLACE` so the
   * notice reads the All Access switch.
   */
  surface?: Surface
}

/**
 * Query hook that determines whether the auto top-up notice should show above
 * the prompt bar: the user can be topped up automatically, has that switch on, and their
 * coin balance is lower than the cost of the next generation. Studio requires a paid
 * Studio AI plan. Marketplace requires All Access, including a trial, because that
 * trial is charged the coin pack with the subscription.
 *
 * When this applies, it takes priority over the generic purchase/subscribe top-up
 * banner (`useTopUpPromptBannerQuery`) — there's nothing to buy, the top-up already
 * happens automatically on generate.
 *
 * The account settings are only fetched for an entitled user whose balance is known to be
 * short of `nextGenerationCost`; logged-out visitors, users without that plan, and anyone
 * with enough coins resolve to hidden without a request. Callers must report an unresolved cost through
 * `isNextGenerationCostPending`, because a cost that is merely unknown cannot be
 * distinguished from a free generation, and guessing shows the lower-priority top-up banner
 * only to replace it once the cost arrives.
 */
export function useAutoTopUpPromptBannerQuery({
  nextGenerationCost,
  isNextGenerationCostPending,
  surface,
}: UseAutoTopUpPromptBannerQueryInput): {
  isPending: boolean
  show: boolean
} {
  const isMarketplace = surface === Surface.MARKETPLACE
  const { data: isLoggedIn, isPending: isLoggedInPending } = useIsLoggedInQuery()
  const { data: isStudioAIUser, isPending: isStudioAIUserPending } = useIsStudioAIUserQuery()
  const { data: hasAllAccess, isPending: isAllAccessPending } = useHasAllAccessQuery()
  const { data: isOnStudioTrial, isPending: isOnStudioTrialPending } = useIsOnAnyTrialQuery(
    Surface.STUDIO,
  )
  const {
    data: coinBalance,
    isPending: isCoinBalancePending,
    isPlaceholderData: isCoinBalancePlaceholder,
  } = useCoinBalance()

  const isBalanceSettled = !isCoinBalancePending && !isCoinBalancePlaceholder
  const isOverspending =
    isBalanceSettled &&
    coinBalance !== undefined &&
    nextGenerationCost !== undefined &&
    coinBalance < nextGenerationCost

  // The settings endpoint answers `enabled: true` regardless of entitlement (STU-9349).
  // Studio matches the coins top-up card: Studio AI and not on trial. Marketplace reads
  // the All Access switch, and an All Access trial is still entitled.
  const marketplaceEntitlement = {
    isPending: isAllAccessPending,
    isEntitled: hasAllAccess === true,
  }
  const studioEntitlement = {
    isPending: isStudioAIUserPending || isOnStudioTrialPending,
    isEntitled: isStudioAIUser === true && isOnStudioTrial === false,
  }
  const { isPending: isEntitlementPending, isEntitled } = isMarketplace
    ? marketplaceEntitlement
    : studioEntitlement

  const canFetchAutoTopUpSettings = isLoggedIn === true && isEntitled

  // An omitted surface is studio. All Access callers pass their own surface.
  const { data: autoTopUpEnabled, isPending: isAutoTopUpSettingsPending } =
    useGetAutoTopUpSettingsQuery(isMarketplace ? { surface: Surface.MARKETPLACE } : {}, {
      enabled: canFetchAutoTopUpSettings && isOverspending,
      select: data => data.settings?.enabled ?? false,
      // Only fetched once balance/cost prove overspending; ignore any earlier cache entry.
      staleTime: 0,
    })

  return resolveAutoTopUpBanner([
    { pending: isLoggedInPending, ready: isLoggedIn === true },
    { pending: isEntitlementPending, ready: isEntitled },
    {
      pending: !isBalanceSettled || isNextGenerationCostPending,
      ready: isOverspending,
    },
    { pending: isAutoTopUpSettingsPending, ready: autoTopUpEnabled === true },
  ])
}

function resolveAutoTopUpBanner(gates: readonly { pending: boolean; ready: boolean }[]): {
  isPending: boolean
  show: boolean
} {
  for (const gate of gates) {
    if (gate.pending) {
      return { isPending: true, show: false }
    }
    if (!gate.ready) {
      return { isPending: false, show: false }
    }
  }

  return { isPending: false, show: true }
}
